import { useEffect, useState } from 'react';
import {
    Checkbox,
    FormControlLabel,
    FormGroup,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingSection } from '../../common/LoadingSection';
import { useRecordContext } from 'react-admin';
import { goApi } from '../../services';

interface Permission {
    id: string;
    type: string;
    name: string;
}

interface Module {
    name: string;
    permissions: Permission[];
}

interface PermissionsListInputProps {
    handleClose: () => void;
}

const SuperAdminPermissionList: React.FC<PermissionsListInputProps> = ({ handleClose }) => {
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState<Module[]>([]);
    const [assignedAccess, setAssignedAccess] = useState<any[]>([]);
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
    const record = useRecordContext();
    const token = localStorage.getItem('token');

    useEffect(() => {
        if (!token || !record?.id) return;

        const fetchAssignedAccess = async () => {
            setLoading(true);
            try {
                const res = await goApi.get(`/roles/organizations/${record.id}/permissions`);
                setAssignedAccess(res?.data || []);
            } catch (err) {
                console.error('Failed to fetch assigned permissions:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAssignedAccess();
    }, [token, record?.id]);

    useEffect(() => {
        if (!token) return;

        const fetchPermissions = async () => {
            setLoading(true);
            try {
                const res = await goApi.get(`/permissions`);
                setPermissions(res?.data || []);
            } catch (err) {
                console.error('Failed to fetch all permissions:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchPermissions();
    }, [token]);

    useEffect(() => {
        if (assignedAccess.length > 0) {
            const assignedPermissionIds = assignedAccess.map((access: any) => access?.permissionId);
            setSelectedPermissions(assignedPermissionIds);
        }
    }, [assignedAccess]);

    const handleModuleSelect = (modulePermissions: Permission[]) => {
        const allPermissionIds = modulePermissions.map(p => p.id);
        const isModuleSelected = allPermissionIds.every(id => selectedPermissions.includes(id));

        setSelectedPermissions(prev =>
            isModuleSelected
                ? prev.filter(id => !allPermissionIds.includes(id))
                : Array.from(new Set([...prev, ...allPermissionIds])),
        );
    };

    const handlePermissionSelect = (id: string) => {
        setSelectedPermissions(prev =>
            prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id],
        );
    };

    const handleSelectAll = () => {
        const allIds = permissions.flatMap(m => m.permissions.map(p => p.id));
        const isAllSelected = allIds.every(id => selectedPermissions.includes(id));

        setSelectedPermissions(isAllSelected ? [] : allIds);
    };

    const handleSubmit = async () => {
        if (!record?.id) return;

        const formData = { permissionIds: selectedPermissions };

        try {
            await goApi.post(`/roles/organizations/${record.id}/permissions`, formData);

            console.log('Permissions assigned successfully!');
            handleClose();
        } catch (err) {
            console.error('Failed to assign permissions:', err);
            alert('Failed to assign permissions. Please try again.');
        }
    };

    const isAllSelected =
        permissions.length > 0 &&
        permissions.every(module =>
            module.permissions.every(p => selectedPermissions.includes(p.id)),
        );

    return (
        <div className="flex flex-col min-w-[50vw] min-h-[50vh] p-4">
            {loading ? (
                <LoadingSection />
            ) : (
                <>
                    <div className="flex justify-start mb-3">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isAllSelected}
                                    indeterminate={!isAllSelected && selectedPermissions.length > 0}
                                    onChange={handleSelectAll}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                    Select All
                                </Typography>
                            }
                        />
                    </div>

                    <div className="flex-1 overflow-auto">
                        {permissions?.map((module, i) => {
                            const modulePermissionIds = module.permissions.map(p => p.id);
                            const isModuleSelected = modulePermissionIds.every(id =>
                                selectedPermissions.includes(id),
                            );

                            return (
                                <Accordion key={i} className="mb-4" disableGutters>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={isModuleSelected}
                                                    indeterminate={
                                                        modulePermissionIds.some(id =>
                                                            selectedPermissions.includes(id),
                                                        ) && !isModuleSelected
                                                    }
                                                    onChange={() =>
                                                        handleModuleSelect(module.permissions)
                                                    }
                                                />
                                            }
                                            label={
                                                <Typography
                                                    sx={{ fontSize: '14px', fontWeight: 500 }}
                                                >
                                                    {module.name}
                                                </Typography>
                                            }
                                        />
                                    </AccordionSummary>

                                    <AccordionDetails sx={{ paddingTop: 0, paddingBottom: 0 }}>
                                        <FormGroup sx={{ paddingLeft: 4 }}>
                                            {module.permissions.map(permission => (
                                                <FormControlLabel
                                                    key={permission.id}
                                                    control={
                                                        <Checkbox
                                                            checked={selectedPermissions.includes(
                                                                permission.id,
                                                            )}
                                                            onChange={() =>
                                                                handlePermissionSelect(
                                                                    permission.id,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label={
                                                        <Typography
                                                            sx={{
                                                                fontSize: '12px',
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            {permission.type}
                                                        </Typography>
                                                    }
                                                />
                                            ))}
                                        </FormGroup>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </div>

                    <div className="flex flex-row justify-end gap-3 mt-4">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            disabled={selectedPermissions.length === 0}
                        >
                            Submit
                        </Button>
                        <Button onClick={handleClose} variant="outlined">
                            Cancel
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default SuperAdminPermissionList;
